import React, { useState } from "react";
import {
    Upload,
    Save,
    Link as LinkIcon,
    Music2,
    Loader2,
    AlertCircle
} from "lucide-react";

// Standard categories for discography releases
const DISCOGRAPHY_CATEGORIES = ["Band Leader", "Sideman", "Producer"];

export default function MusicForm({ initialData = {}, onSubmit, isEditing = false, isLoading = false }) {
    const [errors, setErrors] = useState({});
    
    // Normalize initial category
    const getInitialCategory = (raw) => {
        if (!raw) return "Band Leader";
        const found = DISCOGRAPHY_CATEGORIES.find(c => c.toLowerCase() === String(raw).toLowerCase());
        return found || "Band Leader";
    };

    const [formData, setFormData] = useState({
        title: initialData.title || "",
        year: initialData.year || new Date().getFullYear().toString(),
        category: getInitialCategory(initialData.category || initialData.type),
        feature_artist: initialData.feature_artist || "",
        description: initialData.description || "",
        status: initialData.status || "active",
        streaming: {
            spotify: initialData.spotify_url || "",
            apple: initialData.apple_music_url || "",
            soundcloud: initialData.soundcloud_url || "",
            audiomack: initialData.audiomack_url || ""
        }
    });

    const [coverFile, setCoverFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState(initialData.cover_path || null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.includes(".")) {
            const [parent, child] = name.split(".");
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setErrors(prev => ({ ...prev, cover: "File is too large (max 5MB)" }));
                return;
            }

            setCoverFile(file);
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.cover;
                return newErrors;
            });

            const reader = new FileReader();
            reader.onloadend = () => {
                setCoverPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = "Title is required";
        if (!formData.year) newErrors.year = "Year is required";
        else if (!/^\d{4}$/.test(formData.year)) newErrors.year = "Enter a valid 4-digit year";

        if (!formData.category) newErrors.category = "Category is required";

        if (!isEditing && !coverFile) {
            newErrors.cover = "Cover artwork is required for new releases";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;

        // Prepare data for API (FormData handles file + fields)
        const data = new FormData();
        data.append("title", formData.title);
        data.append("year", formData.year);
        data.append("category", formData.category);
        data.append("type", formData.category); // compatibility alias
        data.append("feature_artist", formData.feature_artist.trim());
        data.append("description", formData.description);
        data.append("status", formData.status);

        // Pass streaming as JSON string as handled by backend
        data.append("streaming", JSON.stringify(formData.streaming));

        if (coverFile) {
            data.append("cover", coverFile);
        }

        onSubmit(data);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Main Info */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white dark:bg-[#1A1A1A] p-6 lg:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "Playfair Display, serif" }}>
                            <Music2 size={20} className="text-jazz-brown" /> Basic Information
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Release Title</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="e.g., Midnight Conversations"
                                    className={`w-full px-4 py-3 rounded-xl border ${errors.title ? 'border-red-500 bg-red-50/10' : 'border-black/5 bg-[#FAFAFA] dark:bg-[#222]'} focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium`}
                                />
                                {errors.title && <p className="text-[10px] text-red-500 mt-1 font-bold uppercase flex items-center gap-1"><AlertCircle size={10} /> {errors.title}</p>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Release Year</label>
                                    <input
                                        type="number"
                                        name="year"
                                        value={formData.year}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 rounded-xl border ${errors.year ? 'border-red-500 bg-red-50/10' : 'border-black/5 bg-[#FAFAFA] dark:bg-[#222]'} focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium`}
                                    />
                                    {errors.year && <p className="text-[10px] text-red-500 mt-1 font-bold uppercase flex items-center gap-1"><AlertCircle size={10} /> {errors.year}</p>}
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Category <span className="text-jazz-brown">*</span></label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 rounded-xl border ${errors.category ? 'border-red-500 bg-red-50/10' : 'border-black/5 bg-[#FAFAFA] dark:bg-[#222]'} focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium text-black dark:text-white`}
                                    >
                                        {DISCOGRAPHY_CATEGORIES.map(cat => (
                                            <option key={cat} value={cat}>
                                                {cat}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.category && <p className="text-[10px] text-red-500 mt-1 font-bold uppercase flex items-center gap-1"><AlertCircle size={10} /> {errors.category}</p>}
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">
                                    Feature / Associated Artist <span className="text-black/40 dark:text-white/30 text-[10px] font-normal font-sans">(Optional)</span>
                                </label>
                                <input
                                    type="text"
                                    name="feature_artist"
                                    value={formData.feature_artist}
                                    onChange={handleChange}
                                    placeholder="e.g., Featuring John Doe"
                                    className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium"
                                />
                                <p className="text-[10px] text-black/40 dark:text-white/30 mt-1 italic">
                                    Optional: This name will appear alongside the title if provided.
                                </p>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Description / Liner Notes</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="4"
                                    placeholder="Write a brief description or liner notes for this release..."
                                    className={`w-full px-4 py-3 rounded-xl border ${formData.description.length > 2000 ? 'border-red-500 bg-red-50/10' : 'border-black/5 bg-[#FAFAFA] dark:bg-[#222]'} focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium resize-none`}
                                />
                                <div className="flex justify-between items-center mt-1">
                                    {formData.description.length > 2000 ? (
                                        <p className="text-[10px] text-red-500 font-bold uppercase flex items-center gap-1"><AlertCircle size={10} /> Description is too long</p>
                                    ) : <div />}
                                    <p className={`text-[10px] font-bold tracking-widest ${formData.description.length > 2000 ? 'text-red-500' : 'text-black/20'}`}>
                                        {formData.description.length} / 2000
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-[#1A1A1A] p-6 lg:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "Playfair Display, serif" }}>
                            <LinkIcon size={20} className="text-jazz-brown" /> Streaming Platforms
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Spotify Link</label>
                                <input
                                    type="url"
                                    name="streaming.spotify"
                                    value={formData.streaming.spotify}
                                    onChange={handleChange}
                                    placeholder="https://open.spotify.com/album/..."
                                    className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Apple Music Link</label>
                                <input
                                    type="url"
                                    name="streaming.apple"
                                    value={formData.streaming.apple}
                                    onChange={handleChange}
                                    placeholder="https://music.apple.com/album/..."
                                    className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">SoundCloud Link</label>
                                <input
                                    type="url"
                                    name="streaming.soundcloud"
                                    value={formData.streaming.soundcloud}
                                    onChange={handleChange}
                                    placeholder="https://soundcloud.com/..."
                                    className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Audiomack Link</label>
                                <input
                                    type="url"
                                    name="streaming.audiomack"
                                    value={formData.streaming.audiomack}
                                    onChange={handleChange}
                                    placeholder="https://audiomack.com/..."
                                    className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Sidebar Actions */}
                <div className="space-y-6">
                    <div className="bg-white dark:bg-[#1A1A1A] p-6 lg:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-4 text-center">Cover Artwork</label>
                        <div
                            className="aspect-square w-full bg-[#FAFAFA] dark:bg-[#222] border-2 border-dashed border-black/5 dark:border-white/10 rounded-2xl flex flex-col items-center justify-center p-2 text-center cursor-pointer hover:border-jazz-brown/30 transition-colors relative group overflow-hidden"
                            onClick={() => document.getElementById("cover-upload").click()}
                        >
                            {coverPreview ? (
                                <>
                                    <img src={coverPreview} alt="Preview" className="w-full h-full object-cover rounded-xl" />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white p-4">
                                        <Upload size={24} className="mb-2" />
                                        <p className="text-[10px] font-bold uppercase tracking-widest">Change Artwork</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Upload size={32} className="text-black/20 mb-3" />
                                    <p className="text-xs font-bold text-black/60 dark:text-white/60 uppercase tracking-widest">Upload Cover</p>
                                    <span className="text-[10px] text-black/20 mt-1">JPEG, PNG up to 5MB</span>
                                </>
                            )}
                            <input
                                id="cover-upload"
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleFileChange}
                            />
                        </div>
                        {errors.cover && <p className="text-[10px] text-red-500 mt-2 font-bold uppercase text-center flex items-center justify-center gap-1"><AlertCircle size={10} /> {errors.cover}</p>}
                    </div>

                    <div className="bg-white dark:bg-[#1A1A1A] p-6 lg:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-4">Availability</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-bold uppercase tracking-widest"
                        >
                            <option value="active">Active / Public</option>
                            <option value="disabled">Disabled / Private</option>
                        </select>
                        <p className="text-xs text-black dark:text-white/50 mt-3 italic">
                            Private releases will not be visible on the public discography page.
                        </p>
                    </div>

                    <div className="space-y-3">
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 bg-black text-white py-4 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all shadow-xl shadow-black/10 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 className="animate-spin" size={18} />
                                    PROCESSING...
                                </span>
                            ) : (
                                <>
                                    <Save size={18} />
                                    {isEditing ? "UPDATE RELEASE" : "PUBLISH RELEASE"}
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => window.history.back()}
                            className="w-full py-4 rounded-xl text-xs font-bold tracking-widest text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white border border-black/5 bg-white dark:bg-[#1A1A1A] transition-all"
                        >
                            CANCEL
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
}
